import apiClient from './api';
import { ApiResponse, PaginationMeta } from '../types';
import type { DocumentExtraction } from './contact.service';

export interface Conversation {
  id: string;
  organization_id: string;
  agent_id: string;
  customer_id: string;
  channel_type: string;
  channel_id: string;
  status: 'active' | 'resolved' | 'escalated' | 'waiting';
  collected_data?: Record<string, unknown>;
  metadata: Record<string, unknown>;
  started_at: string;
  last_message_at: string;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_external_id?: string;
  customer_avatar_url?: string;
  total_tokens?: number;
  total_cost_usd?: number;
}

export interface ConversationMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  sender_type?: 'customer' | 'agent' | 'human' | 'system' | 'internal';
  sender_name?: string;
  agent_name?: string;
  content: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

// Backend message format uses sender_type instead of role
interface BackendMessage {
  id: string;
  conversation_id: string;
  sender_type: 'customer' | 'agent' | 'human' | 'system' | 'internal';
  content: string;
  content_type: string;
  agent_name?: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

function mapBackendMessage(msg: BackendMessage): ConversationMessage {
  const roleMap: Record<string, 'user' | 'assistant' | 'system'> = {
    customer: 'user',
    agent: 'assistant',
    human: 'assistant',
    internal: 'system',
    system: 'system',
  };
  return {
    id: msg.id,
    conversation_id: msg.conversation_id,
    role: roleMap[msg.sender_type] || 'system',
    sender_type: msg.sender_type,
    sender_name: (msg.metadata as any)?.user_name || undefined,
    agent_name: msg.agent_name,
    content: msg.content,
    metadata: msg.metadata || {},
    created_at: msg.created_at,
  };
}

export interface UsageLogEntry {
  id: string;
  conversation_id: string;
  provider: string;
  model: string;
  operation_type: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  agent_id: string | null;
  created_at: string;
}

export type PaginationInfo = PaginationMeta;

export interface ConversationListParams {
  page?: number;
  limit?: number;
  status?: string;
  channel_type?: string;
  channel_id?: string;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  customer_tag?: string;
  has_human_reply?: boolean;
}

class ConversationsService {
  async getConversations(params: ConversationListParams = {}): Promise<{ data: Conversation[]; pagination: PaginationInfo }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status && params.status !== 'all') query.set('status', params.status);
    if (params.channel_type && params.channel_type !== 'all') query.set('channel_type', params.channel_type);
    if (params.channel_id && params.channel_id !== 'all') query.set('channel_id', params.channel_id);
    if (params.search) query.set('search', params.search);
    if (params.sort_by) query.set('sort_by', params.sort_by);
    if (params.sort_dir) query.set('sort_dir', params.sort_dir);
    if (params.customer_tag && params.customer_tag !== 'all') query.set('customer_tag', params.customer_tag);
    if (params.has_human_reply) query.set('has_human_reply', 'true');

    const qs = query.toString();
    const response = await apiClient.get<ApiResponse<Conversation[]>>(`/conversations${qs ? '?' + qs : ''}`);
    return {
      data: response.data.data || [],
      pagination: response.data.meta || { totalItems: 0, totalPages: 0, perPage: 25, currentPage: 1 },
    };
  }

  async getConversation(id: string): Promise<Conversation> {
    const response = await apiClient.get<ApiResponse<Conversation>>(`/conversations/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to get conversation');
  }

  async updateConversation(id: string, data: { metadata?: Record<string, unknown> }): Promise<void> {
    await apiClient.patch(`/conversations/${id}`, data);
  }

  async getMessages(conversationId: string): Promise<ConversationMessage[]> {
    const response = await apiClient.get<ApiResponse<BackendMessage[]>>(`/conversations/${conversationId}/messages`);
    return (response.data.data || []).map(mapBackendMessage);
  }

  async sendMessage(conversationId: string, content: string): Promise<ConversationMessage> {
    const response = await apiClient.post<ApiResponse<any>>(`/conversations/${conversationId}/messages`, { content });
    if (response.data.data) {
      // Backend returns orchestrator result, extract the response
      const result = response.data.data;
      if (result.response) {
        // Orchestrator result shape
        return {
          id: crypto.randomUUID(),
          conversation_id: conversationId,
          role: 'assistant',
          content: result.response,
          metadata: {},
          created_at: new Date().toISOString(),
        };
      }
      // If it's already a message shape
      if (result.sender_type) return mapBackendMessage(result);
      return result;
    }
    throw new Error('Failed to send message');
  }

  async sendHumanMessage(conversationId: string, content: string): Promise<ConversationMessage> {
    const response = await apiClient.post<ApiResponse<any>>(`/conversations/${conversationId}/human-message`, { content });
    if (response.data.data) {
      const result = response.data.data;
      if (result.sender_type) return mapBackendMessage(result);
      return result;
    }
    throw new Error('Failed to send message');
  }

  async getConversationUsageLogs(conversationId: string): Promise<UsageLogEntry[]> {
    const response = await apiClient.get<ApiResponse<UsageLogEntry[]>>(`/usage/conversation/${conversationId}`);
    return response.data.data || [];
  }

  async updateStatus(conversationId: string, status: string): Promise<Conversation> {
    const response = await apiClient.post<ApiResponse<Conversation>>(`/conversations/${conversationId}/status`, { status });
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update status');
  }

  async updateCustomerTags(customerId: string, tags: { is_high_potential?: boolean; is_blocked?: boolean }): Promise<any> {
    const response = await apiClient.patch<ApiResponse<any>>(`/customers/${customerId}/tags`, tags);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update customer tags');
  }

  async getExtractions(conversationId: string): Promise<DocumentExtraction[]> {
    const response = await apiClient.get<ApiResponse<DocumentExtraction[]>>(`/conversations/${conversationId}/extractions`);
    return response.data.data || [];
  }
}

export const conversationsService = new ConversationsService();
